/**
 * "Can we still see **these two** children?" — CAM-05/GDS §36 for a cooperative sitting (D-076(2)).
 *
 * This file is deliberately thin, for the same reason `coop-camera-adapter.ts` is: the ladder it
 * serves is the Phase 3 ladder, and a second implementation of it would be a second set of rungs to
 * keep in step. It owns exactly three things.
 *
 *   1. **Two shipped {@link createTrackingRecoveryMonitor}s, one per lane.** A cooperative lane *is*
 *      a body: Slice B′ hands each lane its own `CameraInputAdapter` fed a frame containing only
 *      that lane's pose, so `PLAYER_LEFT_FRAME`, `adapter.present`, the `trackingLossPauseMs`
 *      debounce, the re-acquisition debounce, the never-acquired episode and the dead-stream episode
 *      all mean per lane precisely what they mean for one child. Nothing about them is re-derived
 *      here, and the single-body file is not modified.
 *   2. **The one signal those monitors structurally cannot see: identity ambiguity.** A lane the
 *      resolver will not attribute retracts *without* announcing a departure (D-079(2), mutant M13
 *      kills the alternative), so `PLAYER_LEFT_FRAME` never arrives and `present` stays true. It is
 *      read here from `laneStatus`, on the same frame clock as everything else, with a sustain
 *      window so that an ordinary crossing — which the Slice 0′ spike measured at ~17 frames of
 *      collapse (D-077(2)(c)) — puts nothing on screen at all.
 *   3. **The composition the two rulings require.** One lost child is a lane fact and nothing more
 *      (D-076(2)(a)): the sitting is not held, the sibling is not slowed, and the keyboard offer is
 *      not raised — because rung 4 turns the camera off for the *whole* sitting, and taking it from
 *      a child who is happily playing is not a recovery. Both lost is the whole-round pause, exactly
 *      as Phase 3 (D-076(2)(b)).
 *
 * **What it deliberately does not do:** it decides nothing about the mission and knows nothing about
 * missions. It reports; the stage composes the pause from it through the same two-cause machinery a
 * grown-up's Pause button uses. The cooperative reading of "a grown-up cannot resume into a camera
 * that cannot see the child" therefore falls out rather than being written again: the camera's hold
 * is `both lanes unreadable`, so it lifts the moment **one** hero is visible and the returning child
 * plays on while the other stays invited.
 */

import {
  COOPERATIVE_RECOVERY_THRESHOLDS,
  type CooperativeRecoveryThresholds,
} from '../../platform/config/camera-lanes';
import { DEFAULT_GAMEPLAY_TUNABLES } from '../../platform/config/tunables';
import type { PlayerId } from '../../shared/types/ids';
import type { GameplayTunables } from '../../shared/types/tunables';
import type { SemanticEventBus } from '../events/semantic-event-bus';
import type { LaneAttribution } from '../pose/lane-resolver';
import type { PoseFrameSource } from './camera-input-adapter';
import {
  createTrackingRecoveryMonitor,
  type FrameStarvationSource,
  type LowPerformanceSignal,
  type TrackingLossReason,
  type TrackingRecoveryMonitor,
  type TrackingRecoveryState,
} from './tracking-recovery';

/**
 * Why a lane, or a sitting, is unreadable.
 *
 * The shipped three, plus the one only two children can produce: the camera can see somebody and
 * cannot say which hero it is. It is never an attribution and never a fault — "we could not tell"
 * is the whole of it (D-076(1)).
 */
export type CooperativeLossReason = TrackingLossReason | 'UNSURE_WHO';

/** One hero's lane, as the recovery surface reads it. Presence and identity only — never quality. */
export interface CooperativeLaneRecovery {
  readonly playerId: PlayerId;
  /** This hero cannot be read right now, and is owed an invitation. */
  readonly lost: boolean;
  readonly reason: CooperativeLossReason | null;
  /**
   * GDS §36's fourth rung, for **this hero alone** (Slice F′, D-076(5)).
   *
   * It is this lane's own shipped monitor's `fallbackOffered` — the identical rung-4 clock a solo
   * child gets, read per lane rather than re-derived. Slice E′ deliberately left this unsurfaced
   * because the only election it had was sitting-wide, and offering that for one absent child would
   * have taken the camera from a sibling who was happily playing (D-082(2)). The per-child election
   * exists now, so the per-child offer can exist too.
   */
  readonly offerKeys: boolean;
}

export interface CooperativeRecoveryState {
  /**
   * What the *sitting* believes, in exactly the shape the shipped stage already renders.
   * `trackingLost` is both lanes and only both lanes.
   */
  readonly sitting: TrackingRecoveryState;
  /** Why the sitting is held, when it is. Null whenever it is not. */
  readonly sittingReason: CooperativeLossReason | null;
  /** Left lane first. Order is presentation only; neither hero is ever ahead of the other. */
  readonly lanes: readonly CooperativeLaneRecovery[];
}

/**
 * The half of the cooperative pair this monitor reads. Structural on purpose: it asks the pair what
 * the **one** lane resolver decided and never re-derives identity itself (recon §B.2's one-owner
 * rule, which is the reason two files in this folder are as thin as they are).
 */
export interface CooperativeRecoverySource {
  readonly playerIds: readonly PlayerId[];
  /** The heroes whose bodies are actually being read — a subset in a mixed sitting (Slice F′). */
  readonly cameraPlayerIds?: readonly PlayerId[] | undefined;
  lane(playerId: PlayerId): { readonly present: boolean } | null;
  laneStatus(playerId: PlayerId): LaneAttribution['kind'];
}

export interface CooperativeTrackingRecoveryMonitorOptions {
  bus: SemanticEventBus;
  /** The cooperative pair whose lanes are being watched. */
  pair: CooperativeRecoverySource;
  /** The frame stream — the monitor's clock and its heartbeat, shared with both lane monitors. */
  frames: PoseFrameSource;
  health?: (() => LowPerformanceSignal) | undefined;
  starvation?: FrameStarvationSource | undefined;
  tunables?: GameplayTunables;
  thresholds?: CooperativeRecoveryThresholds;
  /** Called only when something actually changed. */
  onChange: (state: CooperativeRecoveryState) => void;
}

export interface CooperativeTrackingRecoveryMonitor {
  readonly state: CooperativeRecoveryState;
  /** This hero stopped answering with their body. Their lane is no longer watched (Slice F′). */
  releaseLane(playerId: PlayerId): void;
  dispose(): void;
}

const CALM: TrackingRecoveryState = {
  trackingLost: false,
  lowPerformance: false,
  fallbackOffered: false,
};

export function createCooperativeTrackingRecoveryMonitor(
  options: CooperativeTrackingRecoveryMonitorOptions,
): CooperativeTrackingRecoveryMonitor {
  const { bus, pair, frames, onChange } = options;
  const { pose } = options.tunables ?? DEFAULT_GAMEPLAY_TUNABLES;
  const thresholds = options.thresholds ?? COOPERATIVE_RECOVERY_THRESHOLDS;
  /**
   * Whether this sitting can be **held** by the camera at all (Slice F′, D-076(5)/(2)(a); S4).
   *
   * Derived from the lanes the camera is reading *right now*, never captured: it was a constructor
   * boolean, and `releaseLane` shrank the lanes without touching it, so a sitting that started with
   * both heroes on the camera and became mixed mid-mission could still be paused by the camera —
   * blocking the working keys of the very child who had just been moved onto them.
   *
   * The reasoning is the ruling read at the sitting level: a hold stops the mission for everybody in
   * the room, and in a mixed sitting there is always somebody who can answer — the hero playing on
   * the keys, whose input has nothing whatever to do with the camera. Their sibling's lane still
   * gets everything else: the soft invitation, the reason, and its own rung-4 offer.
   */
  const holdable = (): boolean => playerIds.length === pair.playerIds.length;
  /**
   * The lanes being watched: the heroes actually answering with their bodies (Slice F′).
   *
   * Not `pair.playerIds`, which is both seats whatever they are playing with. A monitor watching a
   * seat nothing reads would report a child as missing for the whole of a sitting they are playing
   * happily on the keys.
   */
  let playerIds = [...(pair.cameraPlayerIds ?? pair.playerIds)];

  let disposed = false;
  let state: CooperativeRecoveryState = {
    sitting: CALM,
    sittingReason: null,
    lanes: playerIds.map((playerId) => ({
      playerId,
      lost: false,
      reason: null,
      offerKeys: false,
    })),
  };

  /** What each lane's own shipped monitor last said. */
  const laneState = new Map<
    PlayerId,
    { state: TrackingRecoveryState; reason: TrackingLossReason | null }
  >(playerIds.map((playerId) => [playerId, { state: CALM, reason: null }]));
  /** When this lane's identity became unreadable, on the frame clock. Null when it is not. */
  const ambiguousSince = new Map<PlayerId, number | null>(
    playerIds.map((playerId) => [playerId, null]),
  );

  /** The frame clock: the timestamp of the most recent frame actually observed. */
  let lastFrameAt: number | null = null;
  /** When the *sitting* — not a lane — became unreadable. */
  let sittingLostSince: number | null = null;

  const laneNow = (playerId: PlayerId): CooperativeLaneRecovery => {
    const lane = laneState.get(playerId) ?? { state: CALM, reason: null };
    const since = ambiguousSince.get(playerId) ?? null;
    const unsure =
      since !== null && lastFrameAt !== null && lastFrameAt - since >= thresholds.ambiguityNoticeMs;

    // GDS §36's fourth rung for this hero alone (Slice F′). Read straight off that lane's own
    // shipped monitor — the identical clock a solo child's offer runs on.
    const offerKeys = lane.state.fallbackOffered;

    if (lane.state.trackingLost) {
      return { playerId, lost: true, reason: lane.reason, offerKeys };
    }
    // Identity ambiguity is the one loss the shipped monitor cannot see, so it is added — and only
    // ever *added*: a lane its own monitor already calls lost keeps that monitor's own reason.
    return unsure
      ? { playerId, lost: true, reason: 'UNSURE_WHO', offerKeys }
      : { playerId, lost: false, reason: null, offerKeys };
  };

  /**
   * Why the *sitting* is held, when every lane is.
   *
   * Precedence rather than a vote: a camera that has stopped producing pictures is the situation for
   * everybody in the room whatever else is true; two children the camera cannot tell apart are asked
   * to stand apart; a room nobody has walked into is invited rather than asked where it went; and
   * anything else is the ordinary "come back" for two.
   */
  const sittingReasonFrom = (lanes: readonly CooperativeLaneRecovery[]): CooperativeLossReason => {
    if (lanes.some((lane) => lane.reason === 'NO_FRAMES')) return 'NO_FRAMES';
    if (lanes.every((lane) => lane.reason === 'UNSURE_WHO')) return 'UNSURE_WHO';
    if (lanes.every((lane) => lane.reason === 'NEVER_ACQUIRED')) return 'NEVER_ACQUIRED';
    return 'ABSENT';
  };

  const publish = (): void => {
    if (disposed) return;

    const lanes = playerIds.map((playerId) => laneNow(playerId));
    /**
     * The sitting is held only when every lane is unreadable **and** the camera may hold this
     * sitting at all (Slice F′). In a mixed sitting it may not: somebody in the room is answering
     * with keys, and stopping the mission would make them wait for hardware they are not using.
     */
    const held = holdable() && lanes.length > 0 && lanes.every((lane) => lane.lost);

    if (!held) sittingLostSince = null;
    else sittingLostSince ??= lastFrameAt;

    const lowPerformance = [...laneState.values()].some((lane) => lane.state.lowPerformance);

    /**
     * GDS §36's fourth rung, for a sitting rather than for a child.
     *
     * `every` rather than `some` on the lanes' own offers is the D-076(2)(a) reading of rung 4: the
     * offer turns the camera off for the whole room, so raising it because one child stepped out
     * would offer to take the camera away from the sibling who is playing. Per-child fallback is
     * D-076(5)/Slice F′, and nobody is stranded in the meantime — both heroes' keys are live for the
     * whole of a camera sitting (CAM-09).
     *
     * The sitting's own duration arm is not decoration: a sustained swap produces **no** lane-level
     * loss at all (§the ambiguity note above), so without it a family whose children ended up on
     * each other's sides would be held indefinitely and never told the keys were there.
     */
    const lanesOffer =
      laneState.size > 0 && [...laneState.values()].every((lane) => lane.state.fallbackOffered);
    const heldLongEnough =
      held &&
      sittingLostSince !== null &&
      lastFrameAt !== null &&
      lastFrameAt - sittingLostSince >= pose.fallbackOfferMs;
    const fallbackOffered =
      (held || lowPerformance) && (heldLongEnough || lanesOffer || state.sitting.fallbackOffered);

    const sitting: TrackingRecoveryState = {
      trackingLost: held,
      lowPerformance,
      fallbackOffered,
    };
    const sittingReason = held ? sittingReasonFrom(lanes) : null;

    const unchanged =
      sitting.trackingLost === state.sitting.trackingLost &&
      sitting.lowPerformance === state.sitting.lowPerformance &&
      sitting.fallbackOffered === state.sitting.fallbackOffered &&
      sittingReason === state.sittingReason &&
      lanes.length === state.lanes.length &&
      lanes.every((lane, index) => {
        const previous = state.lanes[index];
        return (
          previous !== undefined &&
          previous.playerId === lane.playerId &&
          previous.lost === lane.lost &&
          previous.reason === lane.reason &&
          previous.offerKeys === lane.offerKeys
        );
      });
    if (unchanged) return;

    state = { sitting, sittingReason, lanes };
    onChange(state);
  };

  /**
   * One shipped monitor per lane, built exactly as a single-player sitting builds its one.
   *
   * Presence is read from that lane's own adapter rather than from the bus, for the shipped reason:
   * the keyboard adapter announces the same hero on attach (CAM-09) and believing it would mean no
   * lane is ever empty.
   */
  const monitors = new Map<PlayerId, TrackingRecoveryMonitor>(
    playerIds.map((playerId) => [
      playerId,
      createTrackingRecoveryMonitor({
        bus,
        playerId,
        frames,
        presence: () => pair.lane(playerId)?.present ?? false,
        ...(options.health === undefined ? {} : { health: options.health }),
        ...(options.starvation === undefined ? {} : { starvation: options.starvation }),
        ...(options.tunables === undefined ? {} : { tunables: options.tunables }),
        onChange: (next, detail) => {
          laneState.set(playerId, { state: next, reason: detail.reason });
          publish();
        },
      }),
    ]),
  );

  /**
   * The ambiguity watch, on the frame clock.
   *
   * `laneStatus` is read one frame stale — this subscribes *before* the pair does, keeping the
   * shipped ordering rule that a frame bringing a child back reaches the recovery decision before
   * anything that same frame detects can be dropped as an answer given while the mission was held.
   * One frame against a window measured in hundreds of milliseconds is immaterial, and is recorded
   * rather than hidden.
   */
  const releaseFrames = frames.subscribe((frame) => {
    if (disposed) return;
    lastFrameAt = frame.detectedAt;

    for (const playerId of playerIds) {
      if (pair.laneStatus(playerId) === 'ambiguous') {
        if (ambiguousSince.get(playerId) == null) ambiguousSince.set(playerId, frame.detectedAt);
      } else {
        ambiguousSince.set(playerId, null);
      }
    }

    publish();
  });

  return {
    get state(): CooperativeRecoveryState {
      return state;
    },

    /**
     * This hero stopped answering with their body (Slice F′).
     *
     * Their own monitor is disposed and their lane leaves the composition entirely, so nothing
     * carries on watching a seat nothing reads. Everything else is untouched — the sibling's
     * monitor, its clocks, its episode, and the sitting's own both-lost clock — because the whole
     * point of a per-child fallback is that it is not a sitting-wide teardown (D-076(5)).
     */
    releaseLane(playerId: PlayerId): void {
      const monitor = monitors.get(playerId);
      if (monitor === undefined) return;
      monitor.dispose();
      monitors.delete(playerId);
      laneState.delete(playerId);
      ambiguousSince.delete(playerId);
      playerIds = playerIds.filter((id) => id !== playerId);
      publish();
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      releaseFrames();
      for (const monitor of monitors.values()) monitor.dispose();
    },
  };
}
